import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HardHat, Home, Hammer, Building2, Truck } from "lucide-react";

const ROLES = [
  { id: "homeowner", label: "Homeowner", icon: Home, desc: "Post jobs & hire" },
  { id: "contractor", label: "Contractor", icon: HardHat, desc: "Win contracts" },
  { id: "laborer", label: "General Labor", icon: Hammer, desc: "Find gigs" },
  { id: "realtor", label: "Realtor / Investor", icon: Building2, desc: "Deals & properties" },
  { id: "driver", label: "Cargo Driver", icon: Truck, desc: "Deliver materials" },
];

const AuthModal = ({ open, onOpenChange, mode, setMode }) => {
  const { login, register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "homeowner" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      } else {
        const u = await register(form);
        toast.success(`Account created — welcome, ${u.name.split(" ")[0]}!`);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-white/10 max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">
            {mode === "login" ? "Log In" : "Join Exclusive Trades"}
          </DialogTitle>
        </DialogHeader>

        <button onClick={googleLogin} data-testid="auth-google-button"
          className="w-full flex items-center justify-center gap-3 bg-white text-slate-800 font-semibold rounded-md py-2.5 hover:bg-slate-100 transition-colors">
          <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-5 w-5" />
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-1">
          <div className="h-px flex-1 bg-white/10" /><span className="text-xs text-slate-500">OR</span><div className="h-px flex-1 bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <>
              <div>
                <Label className="text-slate-300 text-xs">Full Name / Company</Label>
                <Input value={form.name} onChange={set("name")} required className="bg-secondary border-white/10 mt-1" data-testid="auth-name-input" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs mb-1.5 block">I am a...</Label>
                <div className="grid grid-cols-3 gap-2">
                  {ROLES.map((r) => (
                    <button type="button" key={r.id} onClick={() => setForm({ ...form, role: r.id })}
                      className={`p-2 rounded-md border text-left transition-colors ${form.role === r.id ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/25"}`}
                      data-testid={`auth-role-${r.id}`}>
                      <r.icon className={`h-4 w-4 mb-1 ${form.role === r.id ? "text-primary" : "text-slate-400"}`} />
                      <p className="text-[11px] font-semibold text-white leading-tight">{r.label}</p>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          <div>
            <Label className="text-slate-300 text-xs">Email</Label>
            <Input type="email" value={form.email} onChange={set("email")} required className="bg-secondary border-white/10 mt-1" data-testid="auth-email-input" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Password</Label>
            <Input type="password" value={form.password} onChange={set("password")} required className="bg-secondary border-white/10 mt-1" data-testid="auth-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-white font-semibold" data-testid="auth-submit-button">
            {loading ? "Please wait..." : mode === "login" ? "Log In" : "Create Account"}
          </Button>
        </form>

        <p className="text-center text-sm text-slate-400">
          {mode === "login" ? "New here? " : "Already have an account? "}
          <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-primary font-semibold" data-testid="auth-toggle-mode">
            {mode === "login" ? "Create an account" : "Log in"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
